/**
 * i18n — Lightweight client-side internationalisation
 *
 * Uses SolidJS signals for reactive locale switching.
 * Translations are fetched from the backend API (primary) with bundled
 * TypeScript modules as an instant fallback while the network request
 * is in-flight or when offline.
 *
 * Usage:
 *   import { t, locale, setLocale } from '@/shared/i18n';
 *   <span>{t('common.save')}</span>
 *
 * Interpolation:
 *   t('greeting', { name: 'Viet' })  // "Chào mừng, Viet!"
 */

import { createSignal } from 'solid-js';
import { apiClient } from '@/shared/lib/api-client';
import { en } from './en';
import { vi } from './vi';

// ============================================
// Types
// ============================================

export type Locale = string;

/** Flat dictionary â€” dot-separated keys map to strings */
export type TranslationDict = Record<string, string>;

// ============================================
// Locale dictionaries
// ============================================

/** Bundled fallback dictionaries — always available, no network needed */
const BUNDLED: Record<string, TranslationDict> = { en, vi };

/**
 * Remote dictionary fetched from the backend for the active locale.
 * This is the primary source of truth; bundled files are used while it loads.
 */
const [_remoteDict, _setRemoteDict] = createSignal<TranslationDict | null>(
  null
);

export const LOCALE_LABELS: Record<string, string> = {
  en: 'English',
  vi: 'Ti\u1EBFng Vi\u1EC7t',
};

export const AVAILABLE_LOCALES: string[] = ['vi', 'en'];

// ============================================
// Persisted locale signal
// ============================================

const STORAGE_KEY = 'sm_locale';

/** Currently logged-in user ID â€” used to key per-user locale prefs */
let _currentUserId: string | null = null;

function userLocaleKey(userId: string): string {
  return `${STORAGE_KEY}_${userId}`;
}

function detectBrowserLocale(): Locale | null {
  try {
    // navigator.languages is ordered by user preference
    const candidates = navigator.languages ?? [navigator.language];
    for (const tag of candidates) {
      const parts = tag.split('-');
      if (!parts[0]) continue;
      const lang = parts[0].toLowerCase();
      if (lang === 'en') return 'en';
      if (lang === 'vi') return 'vi';
    }
  } catch {
    // SSR or restricted environment
  }
  return null;
}

/** Public marketing page paths (EN versions, without /vi prefix) */
const PUBLIC_PATHS = [
  '/welcome',
  '/pricing',
  '/features/',
  '/privacy',
  '/terms',
  '/billing-terms',
  '/blogs',
];

/** Returns 'vi' if the current URL path has a /vi/ prefix on a public page. */
function getLocaleFromPath(): Locale | null {
  try {
    const path = window.location.pathname;
    if (!path.startsWith('/vi')) return null;
    // Ensure it's /vi or /vi/...
    if (path === '/vi' || path.startsWith('/vi/')) return 'vi';
  } catch {
    // SSR guard
  }
  return null;
}

/** Returns true if the current path is a public marketing page. */
export function isPublicPage(): boolean {
  try {
    const path = window.location.pathname;
    const stripped = path.startsWith('/vi') ? path.slice(3) || '/' : path;
    return PUBLIC_PATHS.some((p) => stripped === p || stripped.startsWith(p));
  } catch {
    return false;
  }
}

function getInitialLocale(): Locale {
  // URL path is authoritative on public pages (for SEO — Google crawls /vi/* as Vietnamese)
  const pathLocale = getLocaleFromPath();
  if (pathLocale) return pathLocale;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // localStorage unavailable
  }
  return detectBrowserLocale() ?? 'en';
}

const [locale, _setLocale] = createSignal<Locale>(getInitialLocale());

/** Tracks the locale for which we last kicked off a fetch. */
let _fetchingLocale: string | null = null;

/**
 * Fetch translations from the backend for the given locale.
 * On success, replaces the remote dictionary signal.
 * On failure, silently falls back to bundled files (already available via t()).
 */
async function _loadRemoteTranslations(loc: string): Promise<void> {
  _fetchingLocale = loc;
  try {
    const data = await apiClient.get<{ translations: TranslationDict }>(
      `/translation/translations/${encodeURIComponent(loc)}`
    );
    // Only apply if the locale hasn't changed while we were fetching
    if (_fetchingLocale === loc) {
      _setRemoteDict(data.translations ?? null);
    }
  } catch {
    // Backend unavailable — bundled files remain the source
    if (_fetchingLocale === loc) {
      _setRemoteDict(null);
    }
  }
}

export function setLocale(l: Locale): void {
  _setLocale(l);
  // Clear stale remote dict so t() falls back to bundled until fresh data arrives
  _setRemoteDict(null);
  _loadRemoteTranslations(l);
  try {
    // Always update the generic fallback key
    localStorage.setItem(STORAGE_KEY, l);
    // Also persist to the per-user key so this user's preference is remembered
    if (_currentUserId) {
      localStorage.setItem(userLocaleKey(_currentUserId), l);
    }
  } catch {
    // ignore
  }
}

/**
 * Called after a user logs in.
 * Priority: per-user localStorage > generic localStorage > DB locale.
 * localStorage wins because the DB always defaults to 'en' and would otherwise
 * clobber the user's in-app language choice on every login.
 * Also stores the user ID so future setLocale() calls persist per-user.
 */
export function initLocaleForUser(userId: string, dbLocale?: string): void {
  _currentUserId = userId;
  try {
    // Per-user localStorage entry is most authoritative (explicit in-app choice)
    const userStored = localStorage.getItem(userLocaleKey(userId));
    if (userStored) {
      _setLocale(userStored);
      localStorage.setItem(STORAGE_KEY, userStored);
    } else {
      // Generic locale key (set before user was known)
      const genericStored = localStorage.getItem(STORAGE_KEY);
      if (genericStored) {
        _setLocale(genericStored);
        localStorage.setItem(userLocaleKey(userId), genericStored);
      } else if (dbLocale) {
        // Fall back to DB value (only if no local preference exists)
        _setLocale(dbLocale);
        localStorage.setItem(STORAGE_KEY, dbLocale);
        localStorage.setItem(userLocaleKey(userId), dbLocale);
      }
      // No saved preference anywhere -> keep whatever locale is currently active
    }
  } catch {
    // ignore
  }
  // Fetch remote translations for whichever locale was resolved
  _loadRemoteTranslations(locale());
}

/**
 * Called on logout â€” disassociates future locale changes from the user.
 * Does NOT reset the locale itself (leaves the last-used language active).
 */
export function clearUserLocale(): void {
  _currentUserId = null;
}

export { locale };

// ============================================
// Translation function
// ============================================

/**
 * Translate a key, with optional interpolation.
 *
 * Resolution order:
 *   1. Remote dictionary (fetched from backend for the active locale)
 *   2. Bundled dictionary for the active locale
 *   3. Bundled English dictionary
 *   4. The raw key itself
 *
 * @param key    Dot-separated key, e.g. "common.save"
 * @param params Optional interpolation values, e.g. { name: 'Viet' }
 * @returns      Translated string, or the key itself if missing
 */
export function t(
  key: string,
  params?: Record<string, string | number>
): string {
  const remote = _remoteDict();
  const bundled = BUNDLED[locale()] ?? BUNDLED['en'] ?? {};

  const remoteValue = remote?.[key];
  const shouldUseRemote =
    typeof remoteValue === 'string' &&
    remoteValue.trim().length > 0 &&
    remoteValue !== key;

  let value: string | undefined = shouldUseRemote ? remoteValue : bundled[key];

  if (value === undefined) {
    // Fallback to English bundled, then to the raw key
    value = BUNDLED['en']?.[key] ?? key;
  }

  if (params) {
    for (const [k, v] of Object.entries(params)) {
      value = value.replaceAll(`{${k}}`, String(v));
    }
  }

  return value;
}
